import { argbFromHex } from '@material/material-color-utilities'

import { IUpdateBrandKitBody } from '@makyoapp/makyo-sdk/api/BrandKits'
import { IBrandKit, IBrandKitPresenter } from '@makyoapp/makyo-sdk/models/BrandKits'
import { DEFAULT_MATERIAL_TYPOGRAPHY } from '@makyoapp/makyo-tw-ui'

import { BASE_PALETTE_COLOR, BrandKit, MtTypography, MtTypographyItem, MtTypographyKey } from '@/shared/brand-kit'

export function fromSdkBrandKitPresenterList(items: IBrandKitPresenter[]) {
  return items.map(fromSdkBrandKitPresenter)
}

export function fromSdkBrandKitPresenter(item: IBrandKitPresenter): BrandKit {
  return {
    id: item._id,
    name: item._Name,
    logos: item._Logos,
    colors: (
      item._Colors ?? [
        {
          isDefault: true,
          name: 'Default',
          value: argbFromHex(BASE_PALETTE_COLOR[0].color),
          customColors: [],
        },
      ]
    ).map((c) => {
      return {
        isDefault: c._IsDefault,
        name: c._Name,
        value: c._Value,
        customColors: c._CustomColors
          ? c._CustomColors.map((cc) => {
              return {
                name: cc._Name,
                value: cc._Value,
                blend: cc._Blend,
              }
            })
          : [],
      }
    }),
    typography: DEFAULT_MATERIAL_TYPOGRAPHY,
  }
}

export function fromSdkBrandKit(item: IBrandKit): BrandKit {
  const fontKeys = Object.keys(item._FontsFamily ?? {})

  return {
    id: item._id,
    name: item._Name,
    logos: item._Logos,
    colors: (
      item._Colors ?? [
        {
          isDefault: true,
          name: 'Default',
          value: argbFromHex(BASE_PALETTE_COLOR[0].color),
          customColors: [],
        },
      ]
    ).map((c) => {
      return {
        isDefault: c._IsDefault,
        name: c._Name,
        value: c._Value,
        customColors: c._CustomColors
          ? c._CustomColors.map((cc) => {
              return {
                name: cc._Name,
                value: cc._Value,
                blend: cc._Blend,
              }
            })
          : [],
      }
    }),
    isDetailOk: true,
    typography:
      fontKeys.length > 0
        ? (Object.fromEntries(
            fontKeys
              .filter((k) => k !== undefined && k in item._FontsFamily && typeof item._FontsFamily[k] === 'string')
              .map((k) => {
                try {
                  const parsedValue = JSON.parse(item._FontsFamily[k])
                  if (parsedValue && typeof parsedValue === 'object') {
                    return [k as MtTypographyKey, parsedValue as MtTypographyItem]
                  } else {
                    throw new Error(`Invalid parsed value for key: ${k}`)
                  }
                } catch (error) {
                  console.error(`Error parsing key "${k}":`, error)
                  return null
                }
              })
              .filter((entry): entry is [MtTypographyKey, MtTypographyItem] => entry !== null)
          ) as unknown as MtTypography)
        : DEFAULT_MATERIAL_TYPOGRAPHY,
  }
}

export function toSdkBrandKitBody(brand: BrandKit): IUpdateBrandKitBody {
  return {
    _Name: brand.name,
    _Colors: brand.colors.map((c) => {
      return {
        _Value: c.value,
        _Name: c.name,
        _IsDefault: c.isDefault,
        _CustomColors: c.customColors.map((cc) => {
          return {
            _Value: cc.value,
            _Name: cc.name,
            _Blend: cc.blend,
          }
        }),
      }
    }),
    _FontsFamily: {
      displayExtraExtraLarge: JSON.stringify(brand.typography.displayExtraExtraLarge),
      displayExtraLarge: JSON.stringify(brand.typography.displayExtraLarge),
      displayLarge: JSON.stringify(brand.typography.displayLarge),
      displayMedium: JSON.stringify(brand.typography.displayMedium),
      displaySmall: JSON.stringify(brand.typography.displaySmall),
      displayExtraSmall: JSON.stringify(brand.typography.displayExtraSmall),
      displayExtraExtraSmall: JSON.stringify(brand.typography.displayExtraExtraSmall),
      headlineExtraExtraLarge: JSON.stringify(brand.typography.headlineExtraExtraLarge),
      headlineExtraLarge: JSON.stringify(brand.typography.headlineExtraLarge),
      headlineLarge: JSON.stringify(brand.typography.headlineLarge),
      headlineMedium: JSON.stringify(brand.typography.headlineMedium),
      headlineSmall: JSON.stringify(brand.typography.headlineSmall),
      headlineExtraSmall: JSON.stringify(brand.typography.headlineExtraSmall),
      headlineExtraExtraSmall: JSON.stringify(brand.typography.headlineExtraExtraSmall),
      labelExtraExtraLarge: JSON.stringify(brand.typography.labelExtraExtraLarge),
      labelExtraLarge: JSON.stringify(brand.typography.labelExtraLarge),
      labelLarge: JSON.stringify(brand.typography.labelLarge),
      labelMedium: JSON.stringify(brand.typography.labelMedium),
      labelSmall: JSON.stringify(brand.typography.labelSmall),
      labelExtraSmall: JSON.stringify(brand.typography.labelExtraSmall),
      labelExtraExtraSmall: JSON.stringify(brand.typography.labelExtraExtraSmall),
      titleExtraExtraLarge: JSON.stringify(brand.typography.titleExtraExtraLarge),
      titleExtraLarge: JSON.stringify(brand.typography.titleExtraLarge),
      titleLarge: JSON.stringify(brand.typography.titleLarge),
      titleMedium: JSON.stringify(brand.typography.titleMedium),
      titleSmall: JSON.stringify(brand.typography.titleSmall),
      titleExtraSmall: JSON.stringify(brand.typography.titleExtraSmall),
      titleExtraExtraSmall: JSON.stringify(brand.typography.titleExtraExtraSmall),
      bodyExtraExtraLarge: JSON.stringify(brand.typography.bodyExtraExtraLarge),
      bodyExtraLarge: JSON.stringify(brand.typography.bodyExtraLarge),
      bodyLarge: JSON.stringify(brand.typography.bodyLarge),
      bodyMedium: JSON.stringify(brand.typography.bodyMedium),
      bodySmall: JSON.stringify(brand.typography.bodySmall),
      bodyExtraSmall: JSON.stringify(brand.typography.bodyExtraSmall),
      bodyExtraExtraSmall: JSON.stringify(brand.typography.bodyExtraExtraSmall),
      navigationbarPhone: JSON.stringify(brand.typography.navigationbarPhone),
      navigationbarPhoneLandscape: JSON.stringify(brand.typography.navigationbarPhoneLandscape),
      navigationbarTablet: JSON.stringify(brand.typography.navigationbarTablet),
      navigationbarTabletLandscape: JSON.stringify(brand.typography.navigationbarTabletLandscape),
      navigationbarDesktop: JSON.stringify(brand.typography.navigationbarDesktop),
      navigationbarTv: JSON.stringify(brand.typography.navigationbarTv),
      htmlH1: JSON.stringify(brand.typography.htmlH1),
      htmlH2: JSON.stringify(brand.typography.htmlH2),
      htmlH3: JSON.stringify(brand.typography.htmlH3),
      htmlH4: JSON.stringify(brand.typography.htmlH4),
      htmlH5: JSON.stringify(brand.typography.htmlH5),
      htmlH6: JSON.stringify(brand.typography.htmlH6),
      htmlParagraph: JSON.stringify(brand.typography.htmlParagraph),
      htmlOl: JSON.stringify(brand.typography.htmlOl),
      htmlUl: JSON.stringify(brand.typography.htmlUl),
    },
  }
}
